import { useCallback, useEffect, useMemo, useState } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useLocation } from '@marketplace-web/shared/browser'
import { removeParamsFromQuery } from '@marketplace-web/shared/utils'
import {
  getBumpableItems,
  getBumpableItemDataFromModel,
  BumpableItemModel,
  BumpableItemPreCheckoutModel,
  transformBumpableItemsResponse,
} from '@marketplace-web/vas/vas-data'
import {
  getPublicPhotos,
  transformPublicPhotosResponse,
} from '@marketplace-web/domain/public-photos-data'

import useBumpCheckoutContext from '../../../hooks/useBumpCheckoutContext'

const PER_PAGE = 20
const DEFAULT_PAGE = 1
const DEFAULT_PAGE_CLIENT_FETCH = 2

type Props = {
  initialItems?: Array<BumpableItemModel> | null
  isEndReached?: boolean
}

const useBumpableUserItems = ({ initialItems, isEndReached }: Props = {}) => {
  const { replaceHistoryState, relativeUrl, urlQuery, searchParams } = useLocation()

  const { setSelectedItems } = useBumpCheckoutContext()

  const [currentPage, setCurrentPage] = useState(
    initialItems ? DEFAULT_PAGE_CLIENT_FETCH : DEFAULT_PAGE,
  )
  const [endReached, setEndReached] = useState(isEndReached ?? false)
  const [items, setItems] = useState<Array<BumpableItemModel>>(initialItems ?? [])

  const { item_ids: itemIdsParam } = searchParams

  const selectedItemIdInUrl = useMemo(() => {
    if (typeof itemIdsParam === 'string') return null
    if (!itemIdsParam?.length) return null

    return Number(itemIdsParam[0]) || null
  }, [itemIdsParam])

  const { fetch, isLoading } = useFetch(getBumpableItems, transformBumpableItemsResponse)

  const fetchNextPage = useCallback(async () => {
    if (endReached) return

    const { transformedData: bumpableItemsData } = await fetch({
      currentPage,
      perPage: PER_PAGE,
    })

    if (!bumpableItemsData) return

    const { items: bumpableItems, pageInfo } = bumpableItemsData

    setItems(prevItems => [...prevItems, ...bumpableItems])
    setCurrentPage(page => page + 1)
    setEndReached(pageInfo.hasMore === false)
  }, [currentPage, endReached, fetch])

  const fetchUserItemInUrl = useCallback(async () => {
    if (!selectedItemIdInUrl) return

    const response = await getPublicPhotos({
      itemIds: [selectedItemIdInUrl],
    })

    if ('errors' in response) return

    const transformedResponse = transformPublicPhotosResponse(response)
    const itemPhoto = transformedResponse.photos[0]

    if (!itemPhoto) return

    const itemFromUrl: BumpableItemPreCheckoutModel = {
      id: selectedItemIdInUrl,
      title: null,
      thumbnail: itemPhoto.url,
    }

    setSelectedItems([itemFromUrl])
  }, [selectedItemIdInUrl, setSelectedItems])

  const removeSelectedItemIdFromUrl = useCallback(() => {
    const urlWithoutParam = removeParamsFromQuery(relativeUrl, urlQuery, ['item_ids'])
    replaceHistoryState(urlWithoutParam)
  }, [relativeUrl, replaceHistoryState, urlQuery])

  useEffect(() => {
    if (!selectedItemIdInUrl) return
    if (!items.length) return

    const itemInUrl = items.find(item => item.id === selectedItemIdInUrl)

    if (!itemInUrl) {
      fetchUserItemInUrl()
      removeSelectedItemIdFromUrl()

      return
    }

    setSelectedItems([getBumpableItemDataFromModel(itemInUrl)])
    removeSelectedItemIdFromUrl()
  }, [
    fetchUserItemInUrl,
    selectedItemIdInUrl,
    items,
    removeSelectedItemIdFromUrl,
    setSelectedItems,
  ])

  return {
    items,
    isLoading,
    fetchNextPage,
    endReached,
  }
}

export default useBumpableUserItems
